
import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimeControllerProps {
  reminderTimes: string[];
  onChange?: (times: string[]) => void;
  currentTime?: string;
}

export const TimeController = ({ reminderTimes, onChange, currentTime }: TimeControllerProps) => {
  const [currentHour, setCurrentHour] = useState(() => {
    const now = new Date();
    return now.getHours() + now.getMinutes() / 60;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentHour(now.getHours() + now.getMinutes() / 60);
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const timeToAngle = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return ((hours + minutes / 60) / 24) * 360;
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="relative w-full aspect-square max-w-[300px] mx-auto">
          {/* Clock face */}
          <div className="absolute inset-0 rounded-full border-2 border-muted" />
          
          {/* Hour markers */}
          {[...Array(24)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-2 bg-muted"
              style={{
                top: '50%',
                left: '50%',
                transform: `rotate(${i * 15}deg) translateY(-150px)`,
                transformOrigin: 'center center',
              }}
            />
          ))}
          
          {/* Current time indicator */}
          <div
            className="absolute w-1 h-[calc(100%-20px)] origin-bottom bg-primary/50"
            style={{
              bottom: '50%',
              left: 'calc(50% - 2px)',
              transform: `rotate(${currentHour * 15}deg)`,
              transformOrigin: 'bottom center',
            }}
          >
            <div className="w-3 h-3 rounded-full bg-primary -translate-x-1" />
          </div>

          {/* Reminder time indicators */}
          {reminderTimes.map((time, index) => (
            <div
              key={index}
              className="absolute w-2 h-2 bg-primary rounded-full"
              style={{
                top: '5%',
                left: '50%',
                transform: `rotate(${timeToAngle(time)}deg) translateY(-150px)`,
                transformOrigin: 'center 150px',
              }}
            />
          ))}

          {/* Center dot */}
          <div className="absolute top-1/2 left-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary" />
          
          {/* Time display */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-background/80 px-3 py-1 rounded-full shadow-sm">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">
              {String(Math.floor(currentHour)).padStart(2, '0')}:
              {String(Math.floor((currentHour % 1) * 60)).padStart(2, '0')}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
